import React, { Component } from 'react';
import PropTypes from 'prop-types';
import onClickOutside from 'react-onclickoutside';
import Collapse from './Collapse';
import TreeSelector from './TreeSelector';
import AutocompleteInput from './AutocompleteInput';

export class TagSelectInput extends Component {
  static propTypes = {
    defaultValue: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    })),
    noResultsMessage: PropTypes.string,
    open: PropTypes.bool,
    ontology: PropTypes.objectOf(
      PropTypes.arrayOf(PropTypes.shape({
        root: PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
        }),
        children: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
        })),
      })),
    ).isRequired,
    componentRef: PropTypes.func,
    onTagsChange: PropTypes.func,
    className: PropTypes.string,
    required: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    triggerEvents: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
  }

  static defaultProps = {
    defaultValue: [],
    noResultsMessage: "Can't find any results",
    open: false,
    required: false,
    triggerEvents: false,
    componentRef: () => {},
    onTagsChange: () => {},
  }

  constructor(props) {
    super(props);

    this.state = {
      open: props.open,
      selected: props.defaultValue,
      query: [],
      filterActive: false,
      filteredOntology: {},
      ontology: Object.keys(props.ontology).reduce((acc, item) => {
        const category = {
          name: item,
          slug: item.toLowerCase().split(' ').join('_'),
          tags: Object.values(props.ontology[item]).reduce((parentTagsAcc, parentTag) => {
            const tag = {
              id: parentTag.id,
              name: parentTag.name,
              slug: parentTag.slug,
              parent_id: parentTag.parent_id,
              expanded: false,
              tags: Object.values(parentTag.children).reduce((childrenTagsAcc, childrenTag) => {
                childrenTagsAcc[childrenTag.id] = { // eslint-disable-line no-param-reassign
                  id: childrenTag.id,
                  name: childrenTag.name,
                  slug: childrenTag.slug,
                  parent_id: childrenTag.parent_id,
                  tags: {},
                };
                return childrenTagsAcc;
              }, {}),
            };
            parentTagsAcc[parentTag.id] = tag; // eslint-disable-line no-param-reassign
            return parentTagsAcc;
          }, {}),
          expanded: Object.keys(props.ontology).length === 1,
        };

        acc[category.slug] = category;
        return acc;
      }, {}),
    };
  }

  componentDidMount() {
    this.props.componentRef(this);
  }

  // Returns true if the "query" string is included in the "text" string.
  // It's case insensitive and splits the phrases in words. For eg:
  // `search("account leg", "Accounting & Legal")` returns true.
  search(query, text) {
    const lowerQ = query.toLowerCase();
    const lowerT = text.toLowerCase();
    return lowerQ.split(' ').map(term => lowerT.indexOf(term) >= 0).reduce((acc, val) => (acc && val), true);
  }

  handleInputClick = () => {
    this.setState({ open: true });
  }

  handleInputChange = (e) => {
    const q = e.target.value;

    if (q.length !== 0) {
      const filteredOntology = Object.values(this.state.ontology).reduce((categoriesAcc, category) => {
        const categoryToAdd = {
          name: category.name,
          slug: category.slug,
          tags: Object.values(category.tags).reduce((parentTagsAcc, parentTag) => {
            const parentTagToAdd = {
              id: parentTag.id,
              name: parentTag.name,
              slug: parentTag.slug,
              parent_id: parentTag.parent_id,
              expanded: true,
              tags: Object.values(parentTag.tags).reduce((childrenTagsAcc, childrenTag) => {
                if (this.search(q, childrenTag.name)) {
                  childrenTagsAcc[childrenTag.id] = childrenTag; // eslint-disable-line no-param-reassign
                }
                return childrenTagsAcc;
              }, {}),
            };
            if (this.search(q, parentTag.name) || (Object.keys(parentTagToAdd.tags).length > 0)) {
              parentTagsAcc[parentTagToAdd.id] = parentTagToAdd; // eslint-disable-line no-param-reassign
              parentTagsAcc[parentTagToAdd.id].expanded = Object.keys(parentTagToAdd.tags).length > 0; // eslint-disable-line no-param-reassign
            }
            return parentTagsAcc;
          }, {}),
        };
        if (Object.keys(categoryToAdd.tags).length > 0) {
          categoriesAcc[categoryToAdd.name] = categoryToAdd; // eslint-disable-line no-param-reassign
          categoriesAcc[categoryToAdd.name].expanded = true; // eslint-disable-line no-param-reassign
        }
        return categoriesAcc;
      }, {});

      this.setState({ filterActive: true, filteredOntology, query: [q] });
    } else {
      this.setState({ filterActive: false, filteredOntology: {}, query: [] });
    }
  }

  handleClickOutside = () => {
    if (this.state.open) {
      this.setState({ open: false });
    }
  }

  handleRemoveSelectedTag = (tag) => {
    this.handleRemoveTags(tag.ontology_type, [tag]);
  }

  handleAddTags = (category, tags) => {
    let selected = this.state.selected;
    const tagsToAdd = Object.values(tags)
      .filter((tag) => this.state.selected.filter((t) => t.slug === tag.slug).length === 0)
      .map((tag) => {
        return Object.assign({}, tag, {
          tags: undefined, expanded: undefined, selected: undefined, ontology_type: category,
        });
      });

    selected = selected.concat(tagsToAdd);

    // Add the parent tag if every child tag is selected
    if (tags[0].parent_id !== null) {
      const parentTag = this.state.ontology[category].tags[tags[0].parent_id];
      if (!this.state.selected.some((t) => t.slug === parentTag.slug) && selected
        .filter((selectedTag) => Object.values(parentTag.tags)
          .filter((childTag) => childTag.slug === selectedTag.slug).length > 0).length === Object.keys(parentTag.tags).length) {
        selected = selected.concat([Object.assign({}, parentTag, {
          tags: undefined,
          expanded: undefined,
          selected: undefined,
          ontology_type: category,
        })]);
      }
    }

    this.props.onTagsChange(selected);
    this.setState({ selected, ontology: this.state.ontology, filteredOntology: this.state.filteredOntology });
  }

  handleRemoveTags = (category, tags) => {
    let selected = this.state.selected
      .filter((t) => Object.values(tags).filter((tagToRemove) => t.slug === tagToRemove.slug).length === 0);

    // Remove the parent tag if a child tag is deselected
    if (tags[0].parent_id !== null) {
      const parentTag = this.state.ontology[category].tags[tags[0].parent_id];
      selected = selected.filter((t) => t.slug !== parentTag.slug);
    }

    this.props.onTagsChange(selected);
    this.setState({ selected, ontology: this.state.ontology, filteredOntology: this.state.filteredOntology });
  }

  handleTagClick = (category, tag) => {
    if (this.state.selected.filter((t) => t.slug === tag.slug).length === 0) {
      if (tag.children !== undefined && Object.keys(tag.children).length > 0) {
        this.handleAddTags(category, Object.values(tag.children));
      } else {
        this.handleAddTags(category, [tag]);
      }
    } else if (tag.children !== undefined && Object.keys(tag.children).length > 0 &&
      this.state.selected.filter(t => t.slug === tag.slug).length > 0) {
      this.handleRemoveTags(category, Object.values(tag.children));
    } else {
      this.handleRemoveTags(category, [tag]);
    }
  }

  handleCollapseExpandChange = (item) => {
    if (!item.expanded) {
      Object.values(this.state.ontology).forEach((category) => {
        category.expanded = category.slug === item.id ? !item.expanded : false; // eslint-disable-line no-param-reassign
      });
    }

    this.setState({ ontology: this.state.ontology });
  }

  renderCategories() {
    let ontology = this.state.ontology;

    if (this.state.filterActive) {
      ontology = this.state.filteredOntology;
      if (Object.keys(ontology).length === 0) {
        return (
          <li className="u-px-3 u-py-1 ">{this.props.noResultsMessage}</li>
        );
      }
    }

    return Object.values(ontology).map((category) =>
      <li key={category.name} className="u-block">
        <Collapse id={category.slug} label={category.name} expanded={category.expanded} expandedChange={this.handleCollapseExpandChange.bind(this)} triggerType="tree">
          <TreeSelector
            id={category.slug}
            options={Object.values(category.tags).reduce((acc, item) => {
              acc[item.id] = {
                id: item.id,
                name: item.name,
                slug: item.slug,
                parent_id: null,
                expanded: item.expanded,
                children: Object.values(item.tags).reduce((childrenAcc, childrenItem) => {
                  childrenAcc[childrenItem.id] = { // eslint-disable-line no-param-reassign
                    id: childrenItem.id,
                    name: childrenItem.name,
                    slug: childrenItem.slug,
                    parent_id: item.id,
                    children: {},
                  };
                  return childrenAcc;
                }, {}),
              };
              return acc;
            }, {})}
            selected={Object.values(this.state.selected).reduce((acc, item) => {
              acc.push(item.slug);
              return acc;
            }, [])}
            onChange={this.handleTagClick}
            query={this.state.query}
          />
        </Collapse>
      </li>
    );
  }

  renderSelect() {
    const classes = ["TagSelectInput", "u-relative"];
    if (this.props.className) {
      classes.push(this.props.className)
    }
    return (
      <div className={classes.join(' ')}>
        <div className="TagSelectInput-ontology">
          <ul className="TagSelectInput-ontology-categories">
            { this.renderCategories() }
          </ul>
        </div>
      </div>
    );
  }

  render() {
    return (
      <AutocompleteInput
        onChange={this.handleInputChange}
        onClick={this.handleInputClick}
        onClickSelected={this.handleRemoveSelectedTag.bind(this)}
        selected={this.state.selected}
        {...this.props}
      >
        { this.state.open && this.renderSelect() }
      </AutocompleteInput>
    );
  }
}

export default onClickOutside(TagSelectInput);
